import { useMemo, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Container } from '@hivebrite/design-system/Container';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';

import ListFilters from './ListFilters';
import { filterHasValue } from './utils/filtersUtils';
import MoreFiltersModal from '../MoreFiltersModal';

const FiltersContent = ({
  filters,
  filtersByRow,
  filterRows,
  modal,
  onChangeFilters,
  clearFilters,
  values,
  dataTestId,
  actionButtonsCanOverflow,
  forceEnableClearFilters,
  header,
}) => {
  const [modalFiltersOpened, setModalFiltersOpened] = useState(false);

  const maxVisibleFilters = filterRows * filtersByRow;

  const visibleFilters = useMemo(
    () => filters.slice(0, maxVisibleFilters),
    [filters, maxVisibleFilters],
  );
  const hiddenFilters = useMemo(
    () =>
      filters.length > maxVisibleFilters
        ? filters.slice(maxVisibleFilters - 1)
        : [],
    [filters, maxVisibleFilters],
  );
  const filtersValuesSize = useMemo(
    () => filters.filter(filterHasValue(values)).length,
    [values, filters],
  );
  const hiddenFiltersValuesSize = useMemo(
    () => hiddenFilters.filter(filterHasValue(values)).length,
    [values, hiddenFilters],
  );

  const hasHiddenFilters = hiddenFilters.length > 0;
  const filterItems = useMemo(
    () =>
      hasHiddenFilters
        ? visibleFilters.slice(0, maxVisibleFilters - 1)
        : visibleFilters,
    [hasHiddenFilters, maxVisibleFilters, visibleFilters],
  );

  const displayMoreFilters = hasHiddenFilters;
  const displayClearFilters =
    (hasHiddenFilters ||
      actionButtonsCanOverflow ||
      filterItems?.length < maxVisibleFilters) &&
    filterItems?.length > 1;

  const handleChangeFilters = (newValues) => {
    onChangeFilters({ ...values, ...newValues });
    setModalFiltersOpened(false);
  };
  const closeFiltersModal = () => {
    setModalFiltersOpened(false);
  };

  const colWidth = useMemo(
    () => ({
      _: 1,
      m: 1 / filtersByRow,
    }),
    [filtersByRow],
  );

  return (
    <>
      <Container display="block" height="auto" data-testid={dataTestId}>
        <Grid rowGap={5} colGap={{ s: 4, m: 4, l: 4, xl: 6 }} mx={0}>
          {header && (
            <Row>
              <Col>
                <Box font="h2-semibold" mb={4} mt={2}>
                  {header}
                </Box>
              </Col>
            </Row>
          )}
          <Row>
            <ListFilters
              filterItems={filterItems}
              filtersValues={values}
              onChange={handleChangeFilters}
              colWidth={colWidth}
            />
            {(displayMoreFilters || displayClearFilters) && (
              <Col width={colWidth}>
                <Box height="100%" alignItems="flex-end">
                  {displayMoreFilters && (
                    <Button
                      dataTestId="filters-more"
                      icon={() => (
                        <FontAwesomeIcon
                          iconName="sliders"
                          height="16px"
                          iconType={CATEGORIES.SOLID}
                        />
                      )}
                      onClick={() => setModalFiltersOpened(true)}
                      size="large"
                      variant="secondary"
                    >
                      {hiddenFiltersValuesSize === 0
                        ? __('More filters')
                        : __('More filters · %{hiddenFiltersValuesSize}').i__({
                            hiddenFiltersValuesSize,
                          })}
                    </Button>
                  )}
                  {displayClearFilters && (
                    <Box
                      display="block"
                      pl={displayMoreFilters && { _: 4, xl: 6 }}
                    >
                      <Button
                        dataTestId="filters-clear"
                        isDisabled={
                          !forceEnableClearFilters && filtersValuesSize === 0
                        }
                        onClick={clearFilters}
                        size="large"
                      >
                        {__('Clear all filters')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Col>
            )}
          </Row>
        </Grid>
      </Container>
      {modalFiltersOpened && (
        <MoreFiltersModal
          modal={modal}
          onSubmit={handleChangeFilters}
          onCancel={closeFiltersModal}
          hiddenFilters={hiddenFilters}
          filtersValues={values}
        />
      )}
    </>
  );
};

FiltersContent.defaultProps = {
  filtersByRow: 3,
  filterRows: 1,
  actionButtonsCanOverflow: false,
};

export default FiltersContent;
